/**
 * Notification Service
 * Generic notification handling for browser notifications
 */
class NotificationService {
    constructor() {
        this.permission = null;
        this.init();
    }

    /**
     * Initialize the notification service
     */
    async init() {
        await this.requestPermission();
    }

    /**
     * Check if notifications are supported in the browser
     * @returns {boolean}
     */
    isSupported() {
        return "Notification" in window;
    }

    /**
     * Get current notification permission status
     * @returns {string} 'granted', 'denied', or 'default'
     */
    getPermissionStatus() {
        if (!this.isSupported()) return 'denied';
        return Notification.permission;
    }

    /**
     * Request notification permission from the user
     * @returns {Promise<boolean>} True if permission granted
     */
    async requestPermission() {
        if (!this.isSupported()) {
            console.warn("This browser does not support desktop notifications");
            return false;
        }

        // Check current permission state first
        if (Notification.permission === "granted") {
            this.permission = "granted";
            // console.log("Notification permission status:", Notification.permission);
            return true;
        }

        if (Notification.permission === "denied") {
            this.permission = "denied";
            console.log("Notification permission denied");
            return false;
        }

        try {
            // Request permission if it's "default"
            const permission = await Notification.requestPermission();
            this.permission = permission;
            // console.log("Notification permission status:", permission);
            return permission === "granted";
        } catch (error) {
            console.error("Error requesting notification permission:", error);
            return false;
        }
    }

    /**
     * Show a notification
     * @param {string} title - Notification title
     * @param {Object} options - Notification options
     * @param {string} options.body - Notification body text
     * @param {string} options.icon - Notification icon URL
     * @param {string} options.tag - Notification tag to prevent duplicates
     * @param {boolean} options.requireInteraction - Whether notification requires interaction
     * @param {Function} options.onClick - Click handler function
     * @param {number} options.autoClose - Auto close after milliseconds (0 for no auto-close)
     * @returns {Promise<Notification|null>}
     */
    async show(title, options = {}) {
        const {
            body = '',
            icon = '/favicon.ico',
            tag = null,
            requireInteraction = false,
            onClick = null,
            autoClose = 0
        } = options;

        // Check if we have permission
        if (this.getPermissionStatus() === "granted") {
            try {
                const notification = new Notification(title, {
                    body,
                    icon,
                    tag,
                    requireInteraction
                });

                // Handle click event
                if (onClick && typeof onClick === 'function') {
                    notification.onclick = onClick;
                } else {
                    notification.onclick = () => {
                        window.focus();
                    };
                }

                // Auto-close notification
                if (autoClose > 0) {
                    setTimeout(() => {
                        notification.close();
                    }, autoClose);
                }

                console.log("Notification shown successfully");
                return notification;

            } catch (error) {
                console.error("Error creating notification:", error);
                return null;
            }
        } else if (this.getPermissionStatus() === "denied") {
            console.warn("Notifications are blocked. Please enable them in your browser settings.");
            return null;
        } else {
            // Permission is "default", request it
            const granted = await this.requestPermission();
            if (granted) {
                return this.show(title, options); // Retry showing notification
            }
            return null;
        }
    }

    /**
     * Check if user needs to enable notifications and show alert
     * @returns {boolean} True if notifications are available
     */
    checkAndAlertIfBlocked() {
        if (!this.isSupported()) {
            alert("This browser does not support desktop notifications");
            return false;
        }

        if (this.getPermissionStatus() === "denied") {
            alert("Notifications are blocked. Please enable them in your browser settings:\n1. Click the lock icon in the address bar\n2. Enable notifications\n3. Refresh the page");
            return false;
        }

        return true;
    }
}

// Create and export a singleton instance
const notificationService = new NotificationService();

export default notificationService;

// Named exports for convenience
export const {
    show: showNotification,
    requestPermission,
    getPermissionStatus,
    isSupported: isNotificationSupported,
    checkAndAlertIfBlocked
} = notificationService;
